import React from "react";
import {
    AppBar,
    Toolbar,
    IconButton,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    Box,
    Typography,
    Button,
    useMediaQuery,
    useScrollTrigger,
    alpha,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Link as ScrollLink } from "react-scroll";
import DashboardButton from "./DashboardButton";
import { API_URL } from "../config";
import {
    getSharedNavAppBarSx,
    NAVBAR_HEIGHT,
    QR_FLEX_WORDMARK_SX,
    sharedNavToolbarSx,
} from "./navTheme";

const NAV = [
    { label: "Home", to: "home" },
    { label: "Services", to: "services" },
    { label: "Pricing", to: "pricing" },
    { label: "FAQ", to: "faq" },
    { label: "Contact", to: "contact" },
];

const Offset = () => <Box sx={{ height: NAVBAR_HEIGHT }} />;

export default function SubTopHeader2() {
    const isMobile = useMediaQuery("(max-width:960px)");
    const [open, setOpen] = React.useState(false);
    const [loggedIn, setLoggedIn] = React.useState(null);
    const location = useLocation();
    const isOnHome = location.pathname === "/";
    const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 6 });

    React.useEffect(() => {
        fetch(API_URL + "validate_session.php", {
            credentials: "include",
        })
            .then((res) => res.json())
            .then((data) => setLoggedIn(Boolean(data.loggedIn)))
            .catch(() => setLoggedIn(false));
    }, []);

    const headerHeight = isMobile ? NAVBAR_HEIGHT.xs : NAVBAR_HEIGHT.md;
    const scrollOffset = -headerHeight;

    const getRouterTarget = (to) => (to === "home" ? "/" : `/?scroll=${to}`);
    const activeScrollTarget = new URLSearchParams(location.search).get("scroll") || "home";

    const navActionProps = (to) =>
        isOnHome
            ? {
                component: ScrollLink,
                to,
                smooth: true,
                duration: 450,
                offset: scrollOffset,
                spy: true,
                activeClass: "active-link",
            }
            : {
                component: RouterLink,
                to: getRouterTarget(to),
            };

    const isNavItemActive = (to) => {
        if (isOnHome) {
            return to === activeScrollTarget;
        }

        if (to === "home") {
            return location.pathname === "/" && !activeScrollTarget;
        }

        return activeScrollTarget === to;
    };

    return (
        <Box component="header">
            <Box
                component="a"
                href="#main"
                sx={{
                    position: "absolute",
                    left: -9999,
                    top: 0,
                    p: 1,
                    bgcolor: "background.paper",
                    zIndex: 2000,
                    "&:focus": { left: 12 },
                }}
            >
                Skip to content
            </Box>

            <AppBar position="fixed" elevation={0} sx={getSharedNavAppBarSx(scrolled)}>
                <Toolbar sx={sharedNavToolbarSx}>
                    {isMobile && (
                        <IconButton aria-label="open navigation" onClick={() => setOpen(true)} sx={{ color: "text.primary" }}>
                            <MenuRoundedIcon />
                        </IconButton>
                    )}

                    <Typography
                        component={RouterLink}
                        to="/"
                        sx={{
                            ...QR_FLEX_WORDMARK_SX,
                            ml: isMobile ? 0 : 0.5,
                            position: { xs: "absolute", md: "static" },
                            left: { xs: "50%", md: "auto" },
                            transform: { xs: "translateX(-50%)", md: "none" },
                            fontSize: { xs: "1.22rem", md: "1.45rem" },
                        }}
                    >
                        QR Flex
                    </Typography>

                    {!isMobile && (
                        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.8 }}>
                            {NAV.map((item) => (
                                <Button
                                    key={item.to}
                                    {...navActionProps(item.to)}
                                    sx={{
                                        textTransform: "none",
                                        px: 1.7,
                                        py: 1,
                                        borderRadius: 3,
                                        fontWeight: 600,
                                        color: "text.primary",
                                        "&:hover": { bgcolor: alpha("#5b5df0", 0.12), color: "primary.main", transform: "translateY(-1px)" },
                                        "&.active-link": { color: "primary.main", bgcolor: alpha("#5b5df0", 0.18) },
                                    }}
                                >
                                    {item.label}
                                </Button>
                            ))}
                            <DashboardButton />
                        </Box>
                    )}

                    {isMobile && <Box sx={{ width: 40, height: 1 }} />}
                </Toolbar>
            </AppBar>

            <Offset />

            <Drawer
                anchor="left"
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{
                    sx: {
                        width: 320,
                        backdropFilter: "blur(18px)",
                        background: `linear-gradient(180deg, ${alpha("#f8faff", 0.98)} 0%, ${alpha("#eef2ff", 0.96)} 100%)`,
                        borderRight: `1px solid ${alpha("#5b5df0", 0.16)}`,
                    },
                }}
            >
                <Box
                    role="navigation"
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "transparent",
                    }}
                >
                    <Box
                        sx={{
                            px: 2.25,
                            pt: 2,
                            pb: 1.75,
                            background: `linear-gradient(135deg, ${alpha("#5b5df0", 0.12)} 0%, ${alpha("#06b6d4", 0.08)} 100%)`,
                            borderBottom: `1px solid ${alpha("#5b5df0", 0.1)}`,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                            <Box>
                                <Typography sx={{ ...QR_FLEX_WORDMARK_SX, fontSize: "1.18rem" }}>QR Flex</Typography>
                                <Typography sx={{ mt: 0.75, color: "text.secondary", fontSize: "0.92rem", lineHeight: 1.5 }}>
                                    Browse sections faster and jump straight into creating your account.
                                </Typography>
                            </Box>
                            <IconButton
                                aria-label="close navigation"
                                onClick={() => setOpen(false)}
                                sx={{
                                    color: "text.primary",
                                    bgcolor: alpha("#ffffff", 0.66),
                                    border: `1px solid ${alpha("#5b5df0", 0.12)}`,
                                    boxShadow: "0 10px 24px rgba(91, 93, 240, 0.08)",
                                }}
                            >
                                <CloseRoundedIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    <List sx={{ px: 1.5, py: 1.5, display: "grid", gap: 0.8 }}>
                        {NAV.map((item) => {
                            const active = isNavItemActive(item.to);

                            return (
                                <ListItemButton
                                    key={item.to}
                                    {...navActionProps(item.to)}
                                    onClick={() => setOpen(false)}
                                    sx={{
                                        borderRadius: 3,
                                        px: 1.6,
                                        py: 1.15,
                                        border: `1px solid ${active ? alpha("#5b5df0", 0.24) : alpha("#5b5df0", 0.08)}`,
                                        bgcolor: active ? alpha("#5b5df0", 0.14) : alpha("#ffffff", 0.58),
                                        boxShadow: active ? "0 14px 30px rgba(91, 93, 240, 0.12)" : "none",
                                        transition: "transform 180ms ease, background-color 180ms ease, border-color 180ms ease",
                                        '&:hover': {
                                            bgcolor: alpha("#5b5df0", active ? 0.18 : 0.1),
                                            transform: "translateX(4px)",
                                        },
                                    }}
                                >
                                    <ListItemText
                                        primary={item.label}
                                        secondary={active ? "Current section" : "Open section"}
                                        primaryTypographyProps={{
                                            fontWeight: 700,
                                            color: active ? "primary.main" : "text.primary",
                                        }}
                                        secondaryTypographyProps={{
                                            fontSize: "0.76rem",
                                            color: active ? "primary.main" : "text.secondary",
                                        }}
                                    />
                                    <ArrowForwardRoundedIcon sx={{ fontSize: 20, color: active ? "primary.main" : "text.secondary" }} />
                                </ListItemButton>
                            );
                        })}
                    </List>

                    <Box sx={{ p: 2, mt: "auto" }}>
                        <Box
                            sx={{
                                position: "relative",
                                overflow: "hidden",
                                p: 2,
                                borderRadius: 4,
                                background: `linear-gradient(160deg, ${alpha("#ffffff", 0.94)} 0%, ${alpha("#eef2ff", 0.98)} 48%, ${alpha("#e0f2fe", 0.96)} 100%)`,
                                border: `1px solid ${alpha("#5b5df0", 0.12)}`,
                                boxShadow: "0 20px 44px rgba(91, 93, 240, 0.14)",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: "auto -36px -52px auto",
                                    width: 132,
                                    height: 132,
                                    borderRadius: "50%",
                                    background: `radial-gradient(circle, ${alpha("#5b5df0", 0.18)} 0%, ${alpha("#5b5df0", 0)} 72%)`,
                                }}
                            />
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: "-28px auto auto -30px",
                                    width: 96,
                                    height: 96,
                                    borderRadius: "50%",
                                    background: `radial-gradient(circle, ${alpha("#06b6d4", 0.16)} 0%, ${alpha("#06b6d4", 0)} 72%)`,
                                }}
                            />
                            <Box sx={{ position: "relative" }}>
                                <Typography
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        px: 1.1,
                                        py: 0.45,
                                        borderRadius: 999,
                                        fontSize: "0.72rem",
                                        fontWeight: 800,
                                        letterSpacing: "0.08em",
                                        textTransform: "uppercase",
                                        color: "primary.main",
                                        bgcolor: alpha("#5b5df0", 0.1),
                                        border: `1px solid ${alpha("#5b5df0", 0.12)}`,
                                    }}
                                >
                                    Premium access
                                </Typography>
                                <Typography sx={{ mt: 1.35, fontWeight: 900, fontSize: "1.15rem", lineHeight: 1.2 }}>
                                    Ready to launch faster?
                                </Typography>
                                <Typography sx={{ mt: 0.75, mb: 1.8, color: "text.secondary", fontSize: "0.88rem", lineHeight: 1.6 }}>
                                    Create your account to unlock premium QR workflows, campaign tools, and guided onboarding.
                                </Typography>
                                {!loggedIn && (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        component={RouterLink}
                                        to="/auth?mode=signup"
                                        onClick={() => setOpen(false)}
                                        endIcon={<ArrowForwardRoundedIcon />}
                                        sx={{
                                            mb: 1.1,
                                            minHeight: 48,
                                            borderRadius: 999,
                                            fontWeight: 900,
                                            letterSpacing: "0.02em",
                                            background: "linear-gradient(135deg, #5b5df0 0%, #6d5dfc 48%, #4f8cff 100%)",
                                            boxShadow: "0 16px 36px rgba(91, 93, 240, 0.28)",
                                            '&:hover': {
                                                boxShadow: "0 20px 42px rgba(91, 93, 240, 0.34)",
                                                transform: "translateY(-1px)",
                                            },
                                        }}
                                    >
                                        Create your account
                                    </Button>
                                )}
                                <DashboardButton
                                    fullWidthOverride
                                    loggedInOverride={loggedIn === null ? undefined : loggedIn}
                                    stackSx={{ alignItems: "stretch" }}
                                    buttonSx={{
                                        minHeight: 46,
                                        borderRadius: 999,
                                        fontWeight: 800,
                                        borderColor: alpha("#5b5df0", 0.18),
                                        color: "text.primary",
                                        bgcolor: alpha("#ffffff", 0.72),
                                        '&:hover': {
                                            borderColor: alpha("#5b5df0", 0.28),
                                            bgcolor: alpha("#ffffff", 0.92),
                                        },
                                    }}
                                    demoButtonSx={{
                                        minHeight: 46,
                                        fontWeight: 800,
                                        background: "linear-gradient(135deg, #5b5df0 0%, #4f8cff 100%)",
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Drawer>
        </Box>
    );
}
